/** Display thumb stored on the mission file-ref — not a second R2 object. */
export const THUMB_MAX_EDGE = 500;
/** Floor so a 500-wide panorama isn't a 20px-tall smear. Aspect stays put. */
export const THUMB_MIN_PIXELS = 500 * 240;
export const THUMB_MAX_CHARS = 200000;
const SVG_INLINE_MAX = 12 * 1024;
const OLD_JPEG_MAX_CHARS = 25000;
const OLD_WEBP_MAX_CHARS = 40000;

export const sanitizeThumbDataUrl = (value) => {
  if (typeof value !== 'string') return undefined;
  if (!value.startsWith('data:image/')) return undefined;
  if (value.length > THUMB_MAX_CHARS) return undefined;
  return value;
};

/** Old 96px / 384px thumbs look muddy on the widget strip. */
export const thumbNeedsRefresh = (dataUrl) => {
  if (!dataUrl) return true;
  if (!dataUrl.startsWith('data:image/')) return true;
  if (dataUrl.startsWith('data:image/jpeg') && dataUrl.length < OLD_JPEG_MAX_CHARS) {
    return true;
  }
  if (dataUrl.startsWith('data:image/webp') && dataUrl.length < OLD_WEBP_MAX_CHARS) {
    return true;
  }
  return false;
};

export const thumbTargetSize = (width, height) => {
  if (!width || !height) return { width: 1, height: 1 };
  let scale = Math.min(1, THUMB_MAX_EDGE / Math.max(width, height));
  let nextW = Math.max(1, Math.round(width * scale));
  let nextH = Math.max(1, Math.round(height * scale));
  if (nextW * nextH < THUMB_MIN_PIXELS && width * height > nextW * nextH) {
    scale = Math.min(1, Math.sqrt(THUMB_MIN_PIXELS / (width * height)));
    nextW = Math.max(1, Math.round(width * scale));
    nextH = Math.max(1, Math.round(height * scale));
    if (nextW * nextH < THUMB_MIN_PIXELS && nextW < width) nextW += 1;
    if (nextW * nextH < THUMB_MIN_PIXELS && nextH < height) nextH += 1;
  }
  return { width: nextW, height: nextH };
};

const readAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const encodeThumb = (canvas) => {
  try {
    const webp = canvas.toDataURL('image/webp', 0.84);
    if (webp.startsWith('data:image/webp')) {
      const kept = sanitizeThumbDataUrl(webp);
      if (kept) return kept;
    }
  } catch {
    /* WebP can throw in older canvases */
  }
  try {
    return sanitizeThumbDataUrl(canvas.toDataURL('image/jpeg', 0.86)) || null;
  } catch {
    return null;
  }
};

const rasterizeToThumb = (blob) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        let { naturalWidth: width, naturalHeight: height } = img;
        if (!width || !height) {
          URL.revokeObjectURL(url);
          resolve(null);
          return;
        }
        const sized = thumbTargetSize(width, height);
        width = sized.width;
        height = sized.height;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          resolve(null);
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve(encodeThumb(canvas));
      } catch {
        URL.revokeObjectURL(url);
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

/** Build a display thumb from a local File. Non-images return null. */
export const makeImageThumbnail = async (file) => {
  if (!file?.type?.startsWith('image/')) return null;
  try {
    if (file.type === 'image/svg+xml' && file.size <= SVG_INLINE_MAX) {
      const dataUrl = await readAsDataUrl(file);
      return sanitizeThumbDataUrl(dataUrl) || null;
    }
    return rasterizeToThumb(file);
  } catch {
    return null;
  }
};

export const makeImageThumbnailFromUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type || !blob.type.startsWith('image/')) {
      return rasterizeToThumb(blob);
    }
    return makeImageThumbnail(new File([blob], 'thumb', { type: blob.type }));
  } catch {
    return null;
  }
};

const VIDEO_THUMB_TIMEOUT_MS = 10000;

const seekTime = (duration) => {
  if (!Number.isFinite(duration) || duration <= 0) return 0.1;
  if (duration <= 1) return Math.min(0.1, duration / 2);
  return Math.min(1, duration * 0.12);
};

const drawVideoFrame = (video) => {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;
  const sized = thumbTargetSize(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = sized.width;
  canvas.height = sized.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(video, 0, 0, sized.width, sized.height);
  return encodeThumb(canvas);
};

/** Grab one decoded frame and encode it the same way as image thumbs. */
export const captureVideoFrame = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(null), VIDEO_THUMB_TIMEOUT_MS);

    video.onerror = () => finish(null);
    video.onloadeddata = () => {
      const at = seekTime(video.duration);
      const grab = () => finish(drawVideoFrame(video));
      video.onseeked = grab;
      try {
        if (Math.abs((video.currentTime || 0) - at) < 0.05) {
          grab();
          return;
        }
        video.currentTime = at;
      } catch {
        grab();
      }
    };

    video.src = src;
    video.load();
  });

const isVideoFile = (file) =>
  Boolean(
    file?.type?.startsWith('video/') || /\.(mp4|webm|mov|m4v|qt)$/i.test(file?.name || '')
  );

export const makeVideoThumbnail = async (file) => {
  if (!isVideoFile(file)) return null;
  const url = URL.createObjectURL(file);
  try {
    return await captureVideoFrame(url);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const makeVideoThumbnailFromUrl = async (url) => {
  if (!url) return null;
  try {
    return await captureVideoFrame(url);
  } catch {
    return null;
  }
};

/** Image or video → display thumb. Other types return null. */
export const makeMediaThumbnail = async (file) => {
  if (file?.type?.startsWith('image/')) return makeImageThumbnail(file);
  if (isVideoFile(file)) return makeVideoThumbnail(file);
  return null;
};
